import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  FormDialog,
  Modal,
  Toast,
  NotificationsList,
  DropdownMenu,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsBoxArrowUpRight, BsTrash } from "react-icons/bs";
import { MdAdd } from "react-icons/md";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import { usePppAiContext } from "../hooks/usePppAiContext";

const STATO_CFG = {
  bozza:        { key: "ppp_dip_stato_bozza",        cls: "bg-gray-100 text-gray-600" },
  in_revisione: { key: "ppp_dip_stato_in_revisione",  cls: "bg-amber-100 text-amber-800" },
  approvato:    { key: "ppp_dip_stato_approvato",     cls: "bg-emerald-100 text-emerald-700" },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { key: stato, cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(cfg.key) ?? stato}
    </span>
  );
}

function formatEuro(val) {
  if (!val) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(val));
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

export default function DipList() {
  const navigate = useNavigate();
  const { _locate, setOpenForm, addModal, addToast } = useStateContext();
  const { can } = useAuthUser();
  usePppAiContext('ppp.dip');
  const api = usePppApi();

  const [dipList, setDipList]           = useState([]);
  const [procedimenti, setProcedimenti] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [formErrors, setFormErrors]     = useState(null);
  const [form, setForm] = useState({
    oggetto: "", procedimentoId: "", statoDeiLuoghi: "", obiettiviFabbisogni: "",
    livelliServizio: "", requisitiTecnici: "", limiteEconomico: "",
    coberturaFinanziaria: "", haDocfap: false, numeroDocfap: "",
    dataDecisioneAvvio: "", dataTermineRedazione: "", note: "",
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setCheck = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const loadData = useCallback(() => {
    setLoading(true);
    api.listDip({}, {
      onSuccess: (data) => { setDipList(data.dip ?? data); setLoading(false); },
      onError:   () => setLoading(false),
    });
    api.listProcedimenti({ onSuccess: (data) => setProcedimenti(data ?? []) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleCrea = () => {
    if (!form.oggetto) return;
    setSaving(true);
    setFormErrors(null);
    api.creaDip({
      ...form,
      procedimentoId: form.procedimentoId ? parseInt(form.procedimentoId) : null,
      limiteEconomico: form.limiteEconomico ? parseFloat(form.limiteEconomico) : null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_dip_creato")} />);
        setSaving(false);
        setOpenForm(false);
        setFormErrors(null);
        setForm({ oggetto: "", procedimentoId: "", statoDeiLuoghi: "", obiettiviFabbisogni: "", livelliServizio: "", requisitiTecnici: "", limiteEconomico: "", coberturaFinanziaria: "", haDocfap: false, numeroDocfap: "", dataDecisioneAvvio: "", dataTermineRedazione: "", note: "" });
        loadData();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setFormErrors(response.errors); },
    });
  };

  const handleElimina = (dip) => {
    addModal(
      <Modal
        title={_locate("ppp_dip_elimina")}
        content={_locate("ppp_dip_elimina_msg")}
        confirm={_locate("ppp_dip_elimina")}
        style="danger"
        onClick={() => {
          api.eliminaDip(dip.id, {
            onSuccess: () => {
              addToast(<Toast duration={4} content={_locate("ppp_dip_eliminato")} />);
              loadData();
            },
          });
        }}
      />
    );
  };

  const cols = [
    { name: "action",               label: "",                                          size: "w-10 max-w-[40px]", orderable: 0 },
    { name: "oggetto",              label: _locate("ppp_dip_oggetto") },
    { name: "stato",                label: _locate("ppp_dip_stato"),                   size: "w-32" },
    { name: "limiteEconomico",      label: _locate("ppp_dip_limite_economico"),        size: "w-36" },
    { name: "dataTermineRedazione", label: _locate("ppp_dip_data_termine_redazione"),  size: "w-36" },
    { name: "docfap",               label: "DOCFAP",                                   size: "w-20" },
  ];

  const cells_style = {
    action: (row) => (
      <DropdownMenu
        menu_items={[
          <button
            key="apri"
            onClick={() => navigate(`/ppp/dip/${row.id}`)}
            className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <BsBoxArrowUpRight />
            <span>{_locate("ppp_apri")}</span>
          </button>,
          row.stato !== "approvato" && (
            <button
              key="elimina"
              onClick={() => handleElimina(row)}
              className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              <BsTrash />
              <span>{_locate("ppp_dip_elimina")}</span>
            </button>
          ),
        ].filter(Boolean)}
      />
    ),
    oggetto: (row) => (
      <div className="cursor-pointer" onClick={() => navigate(`/ppp/dip/${row.id}`)}>
        <div className="text-xs font-bold text-[#555454] dark:text-dark-text-normal hover:text-gray-400">
          {row.oggetto}
        </div>
        {row.procedimentoTitolo && (
          <div className="text-xs text-[#828c95] mt-0.5">{row.procedimentoTitolo}</div>
        )}
      </div>
    ),
    stato:                (row) => <StatoBadge stato={row.stato} _locate={_locate} />,
    limiteEconomico:      (row) => <span className="text-xs font-mono">{formatEuro(row.limiteEconomico)}</span>,
    dataTermineRedazione: (row) => <span className="text-xs">{formatDate(row.dataTermineRedazione)}</span>,
    docfap: (row) => row.haDocfap
      ? <span className="text-xs font-medium text-orange-500">Sì</span>
      : <span className="text-xs text-gray-400">—</span>,
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {_locate("ppp_dip_titolo_lista")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Art. 3 Allegato I.7 D.lgs 36/2023</p>
        </div>
        {can('ppp.dip.create') && (
          <SimpleButton customFunc={() => setOpenForm(true)} text={<span className="flex items-center gap-1"><MdAdd size={16} />{_locate("ppp_dip_nuovo")}</span>} />
        )}
      </div>

      <Table
        id="ppp-dip"
        dataset={dipList}
        cols={cols}
        cells_style={cells_style}
        loading={loading}
      />
      </div>

      <FormDialog title={_locate("ppp_dip_nuovo")} size="w-[640px]">
        <div className="space-y-4">
          <Input label={_locate("ppp_dip_oggetto")} required value={form.oggetto} onChange={set("oggetto")} />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {_locate("ppp_dip_procedimento")}
            </label>
            <Select
              value={form.procedimentoId}
              onChange={(e) => setForm((f) => ({ ...f, procedimentoId: e.target.value }))}
              options={[
                { value: "", label: _locate("ppp_dip_procedimento_placeholder") },
                ...(procedimenti ?? []).map((p) => ({ value: p.id, label: p.titolo })),
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label={_locate("ppp_dip_data_decisione_avvio")} type="date" value={form.dataDecisioneAvvio} onChange={set("dataDecisioneAvvio")} />
            <Input label={_locate("ppp_dip_data_termine_redazione")} type="date" value={form.dataTermineRedazione} onChange={set("dataTermineRedazione")} />
          </div>

          <TextArea label={_locate("ppp_dip_stato_dei_luoghi")} rows={3} value={form.statoDeiLuoghi} onChange={set("statoDeiLuoghi")} placeholder={_locate("ppp_dip_stato_dei_luoghi_hint")} />
          <TextArea label={_locate("ppp_dip_obiettivi_fabbisogni")} rows={3} value={form.obiettiviFabbisogni} onChange={set("obiettiviFabbisogni")} />
          <TextArea label={_locate("ppp_dip_livelli_servizio")} rows={3} value={form.livelliServizio} onChange={set("livelliServizio")} />
          <TextArea label={_locate("ppp_dip_requisiti_tecnici")} rows={3} value={form.requisitiTecnici} onChange={set("requisitiTecnici")} />

          <div className="grid grid-cols-2 gap-4">
            <Input label={_locate("ppp_dip_limite_economico")} type="number" min={0} step={1000} value={form.limiteEconomico} onChange={set("limiteEconomico")} />
            <TextArea label={_locate("ppp_dip_copertura_finanziaria")} rows={2} value={form.coberturaFinanziaria} onChange={set("coberturaFinanziaria")} />
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="haDocfap" checked={form.haDocfap} onChange={setCheck("haDocfap")} className="w-4 h-4 text-blue-600 rounded border-gray-300" />
            <label htmlFor="haDocfap" className="text-sm text-gray-700 dark:text-gray-300">
              {_locate("ppp_dip_ha_docfap")}
            </label>
          </div>
          {form.haDocfap && (
            <Input label={_locate("ppp_dip_numero_docfap")} value={form.numeroDocfap} onChange={set("numeroDocfap")} required />
          )}

          <TextArea label={_locate("ppp_dip_note")} rows={2} value={form.note} onChange={set("note")} />

          <NotificationsList errors={formErrors} />

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_annulla")} />
            <LoadingButton isLoading={saving} customFunc={form.oggetto ? handleCrea : undefined} text={_locate("ppp_dip_salva")} />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
